import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Play, Pause, Loader2, MapPin } from "lucide-react";
import api from "@/lib/api";

/**
 * ClockInOutWidget — compact clock-in / clock-out control for the
 * sidebar footer. Visible only for ``atendimento`` and ``gestor`` roles;
 * the backend also enforces this, so hiding is purely cosmetic.
 *
 * Behavior:
 *   * On mount, calls ``/api/time-clock/me/status`` to prime the state
 *     (avoids flash of wrong button). Polls every 60s + on window focus.
 *   * Clicking the button asks the browser for GPS via
 *     ``navigator.geolocation.getCurrentPosition``. If the user blocks
 *     GPS the click is aborted with a toast — the backend requires it.
 *   * While clocked in, shows a running "há Xh Ym" chip so the user
 *     can eye-ball their session length without opening a report.
 */
export default function ClockInOutWidget({ user }) {
  const isClockingRole = user && (user.role === "atendimento" || user.role === "gestor");
  const [status, setStatus] = useState({ applicable: false, clocked_in: false, since: null });
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState("");

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/time-clock/me/status");
      setStatus(r.data || {});
    } catch { /* silent — button just stays disabled */ }
  }, []);

  // Prime + poll every 60s + refresh on focus.
  useEffect(() => {
    if (!isClockingRole) return;
    refresh();
    const it = setInterval(refresh, 60000);
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(it); window.removeEventListener("focus", onFocus); };
  }, [isClockingRole, refresh]);

  // Elapsed clock — ticks every 30s.
  useEffect(() => {
    if (!status.clocked_in || !status.since) { setElapsed(""); return; }
    const tick = () => {
      const diffMs = Date.now() - new Date(status.since).getTime();
      const h = Math.floor(diffMs / 3_600_000);
      const m = Math.floor((diffMs % 3_600_000) / 60_000);
      setElapsed(h > 0 ? `${h}h ${m}m` : `${m}m`);
    };
    tick();
    const it = setInterval(tick, 30_000);
    return () => clearInterval(it);
  }, [status.clocked_in, status.since]);

  if (!isClockingRole) return null;

  const grabLocation = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      return reject(new Error("Este browser não suporta GPS."));
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
        captured_at: new Date(pos.timestamp).toISOString(),
      }),
      (err) => {
        const msg = err.code === 1
          ? "Permissão de GPS negada. Ativa a localização no browser para bater ponto."
          : err.code === 2
          ? "GPS indisponível de momento. Tenta em local com melhor sinal."
          : "Tempo esgotado a obter localização. Tenta de novo.";
        reject(new Error(msg));
      },
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 0 },
    );
  });

  const doClock = async (kind) => {
    setBusy(true);
    try {
      const location = await grabLocation();
      await api.post(`/time-clock/${kind}`, { location });
      toast.success(kind === "in" ? "Ponto de entrada registado" : "Ponto de saída registado");
      await refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Erro a bater ponto.";
      toast.error(detail);
    } finally { setBusy(false); }
  };

  return (
    <div className="mb-3 rounded-lg border border-border bg-card p-2.5" data-testid="clock-widget">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-heading font-semibold">
          Ponto
        </div>
        {status.clocked_in && (
          <div className="text-[10px] font-medium text-[hsl(var(--success))] tabular-nums" data-testid="clock-elapsed">
            {elapsed}
          </div>
        )}
      </div>
      {status.clocked_in ? (
        <button
          type="button"
          disabled={busy}
          onClick={() => doClock("out")}
          data-testid="clock-out-btn"
          className="w-full inline-flex items-center justify-center gap-1.5 h-8 rounded-md bg-[hsl(var(--danger-bg))] border border-[hsl(var(--danger))]/25 text-[hsl(var(--danger))] text-[12px] font-medium hover:bg-[hsl(var(--danger-bg))] transition-colors disabled:opacity-60 font-heading"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Pause className="w-3.5 h-3.5" />}
          Bater saída
        </button>
      ) : (
        <button
          type="button"
          disabled={busy}
          onClick={() => doClock("in")}
          data-testid="clock-in-btn"
          className="w-full inline-flex items-center justify-center gap-1.5 h-8 rounded-md bg-[hsl(var(--success-bg))] border border-[hsl(var(--success))]/25 text-[hsl(var(--success))] text-[12px] font-medium hover:bg-[hsl(var(--success-bg))] transition-colors disabled:opacity-60 font-heading"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
          Bater entrada
        </button>
      )}
      {!status.clocked_in && (
        <div className="text-[9.5px] text-muted-foreground mt-1.5 flex items-center gap-1">
          <MapPin className="w-2.5 h-2.5" /> GPS obrigatório
        </div>
      )}
    </div>
  );
}
